import { create } from 'zustand';
import { SystemPrompt } from '@/types/api';
import * as systemPromptApi from '@/lib/api/systemPrompt';
import { toast } from 'sonner';

interface SystemPromptState {
  // Core data
  systemPrompts: SystemPrompt[];
  isSystemPromptsLoading: boolean;
  isInitialized: boolean;
  error: string | null;

  // Editing state
  editingPromptId: string | 'new' | null;
  editingPromptContent: string | null;
  previewResult: any;
  isPreviewLoading: boolean;

  // Legacy tool names (deprecated, use availableTools instead)
  toolNames: string[];
  isToolNamesLoading: boolean;

  // Tool configuration state
  availableTools: string[];
  editingPromptEnabledTools: string[] | 'all';

  // Core actions
  setEditingPromptId: (id: string | 'new' | null) => void;
  fetchPromptForEditing: (id: string) => Promise<void>;
  fetchSystemPrompts: () => Promise<void>;
  createSystemPrompt: (payload: any) => Promise<SystemPrompt | null>;
  updateSystemPrompt: (id: string, payload: any) => Promise<SystemPrompt | null>;
  deleteSystemPrompt: (id: string) => Promise<void>;
  duplicateSystemPrompt: (id: string) => Promise<SystemPrompt | null>;
  setDefaultSystemPrompt: (id: string) => Promise<void>;
  previewSystemPrompt: (payload: any) => Promise<void>;
  clearPreview: () => void;
  fetchToolNames: () => Promise<void>;

  // Tool configuration actions
  fetchAvailableTools: () => Promise<void>;
  setEditingPromptEnabledTools: (tools: string[] | 'all') => void;
  setSystemPromptEnabledTools: (id: string, tools: string[] | 'all') => Promise<void>;
  toggleSystemPromptTool: (id: string, toolName: string) => Promise<void>;
}

export const useSystemPromptStore = create<SystemPromptState>((set, get) => ({
  // Initial state
  systemPrompts: [],
  isSystemPromptsLoading: false,
  isInitialized: false,
  error: null,
  editingPromptId: null,
  editingPromptContent: null,
  previewResult: null,
  isPreviewLoading: false,
  toolNames: [],
  isToolNamesLoading: false,
  availableTools: [],
  editingPromptEnabledTools: 'all',

  setEditingPromptId: (id) => {
    set({ editingPromptId: id });
    if (id === null) {
      set({ editingPromptContent: null }); // Clear raw content when editor is closed
    }
  },

  fetchPromptForEditing: async (id: string) => {
    set({ isSystemPromptsLoading: true, error: null });
    try {
      const rawPrompt = await systemPromptApi.getSystemPromptRaw(id);
      let enabledTools: string[] | 'all' = 'all';

      // The backend returns enabledTools as a string (either 'all' or JSON stringified array)
      // We need to parse it to get the actual string[] | 'all' type
      const rawEnabledTools = rawPrompt.enabledTools;

      if (!rawEnabledTools) {
        enabledTools = 'all';
      } else if (rawEnabledTools === 'all') {
        enabledTools = 'all';
      } else if (typeof rawEnabledTools === 'string') {
        try {
          const parsed = JSON.parse(rawEnabledTools);
          if (Array.isArray(parsed)) {
            enabledTools = parsed;
          } else {
            enabledTools = 'all';
          }
        } catch (e) {
          console.error('[SystemPromptStore] Failed to parse enabledTools:', rawEnabledTools, e);
          enabledTools = 'all';
        }
      } else if (Array.isArray(rawEnabledTools)) {
        // Already parsed (unlikely from backend but handle it)
        enabledTools = rawEnabledTools;
      }

      set({
        editingPromptId: id,
        editingPromptContent: rawPrompt.prompt_content,
        editingPromptEnabledTools: enabledTools,
        isSystemPromptsLoading: false,
      });
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : `Failed to fetch prompt for editing.`;
      set({ error: errorMsg, isSystemPromptsLoading: false });
      toast.error(errorMsg);
    }
  },

  fetchSystemPrompts: async () => {
    // Skip if already initialized or currently loading (prevents duplicate fetch in Strict Mode)
    const { isInitialized, isSystemPromptsLoading } = get();
    if (isInitialized || isSystemPromptsLoading) return;
    
    set({ isSystemPromptsLoading: true, error: null });
    try {
      const prompts = await systemPromptApi.getSystemPrompts();
      set({ systemPrompts: prompts, isSystemPromptsLoading: false, isInitialized: true });
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to fetch system prompts.';
      set({ error: errorMsg, isSystemPromptsLoading: false });
      toast.error(errorMsg);
    }
  },

  createSystemPrompt: async (payload) => {
    set({ isSystemPromptsLoading: true, error: null });
    try {
      const newPrompt = await systemPromptApi.createSystemPrompt(payload);
      set((state) => ({
        systemPrompts: [...state.systemPrompts, newPrompt].sort((a, b) =>
          a.prompt_name.localeCompare(b.prompt_name)
        ),
        isSystemPromptsLoading: false,
      }));
      toast.success('System prompt created successfully.');
      return newPrompt;
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to create system prompt.';
      set({ error: errorMsg, isSystemPromptsLoading: false });
      toast.error(errorMsg);
      return null;
    }
  },

  updateSystemPrompt: async (id, payload) => {
    set({ isSystemPromptsLoading: true, error: null });
    try {
      const updatedPrompt = await systemPromptApi.updateSystemPrompt(id, payload);
      // After updating, refetch the main list to get the injected content for display
      await get().fetchSystemPrompts();
      set({ isSystemPromptsLoading: false });
      toast.success('System prompt updated successfully.');
      return updatedPrompt;
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to update system prompt.';
      set({ error: errorMsg, isSystemPromptsLoading: false });
      toast.error(errorMsg);
      return null;
    }
  },

  deleteSystemPrompt: async (id: string) => {
    const originalPrompts = get().systemPrompts;
    set((state) => ({
      systemPrompts: state.systemPrompts.filter((p) => p.id !== id),
      isSystemPromptsLoading: true,
    }));
    try {
      await systemPromptApi.deleteSystemPrompt(id);
      toast.success('System prompt deleted successfully.');
      set({ isSystemPromptsLoading: false });
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to delete system prompt.';
      set({ error: errorMsg, systemPrompts: originalPrompts, isSystemPromptsLoading: false });
      toast.error(errorMsg);
    }
  },

  duplicateSystemPrompt: async (id: string) => {
    set({ isSystemPromptsLoading: true, error: null });
    try {
      const newPrompt = await systemPromptApi.duplicateSystemPrompt(id);
      set((state) => ({
        systemPrompts: [...state.systemPrompts, newPrompt].sort((a, b) =>
          a.prompt_name.localeCompare(b.prompt_name)
        ),
        isSystemPromptsLoading: false,
      }));
      toast.success(`System prompt duplicated as "${newPrompt.prompt_name}".`);
      return newPrompt;
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to duplicate system prompt.';
      set({ error: errorMsg, isSystemPromptsLoading: false });
      toast.error(errorMsg);
      return null;
    }
  },

  setDefaultSystemPrompt: async (id: string) => {
    set({ isSystemPromptsLoading: true, error: null });
    try {
      await systemPromptApi.setDefaultSystemPrompt(id);
      set((state) => ({
        systemPrompts: state.systemPrompts.map((p) => ({
          ...p,
          is_default: p.id === id,
        })),
        isSystemPromptsLoading: false,
      }));
      toast.success('Default system prompt updated.');
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to set default system prompt.';
      set({ error: errorMsg, isSystemPromptsLoading: false });
      toast.error(errorMsg);
    }
  },

  previewSystemPrompt: async (payload) => {
    set({ isPreviewLoading: true, error: null, previewResult: null });
    try {
      const result = await systemPromptApi.previewSystemPrompt(payload);
      set({ previewResult: result, isPreviewLoading: false });
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to preview system prompt.';
      set({ error: errorMsg, isPreviewLoading: false });
      toast.error(errorMsg);
    }
  },

  clearPreview: () => {
    set({ previewResult: null });
  },

  fetchToolNames: async () => {
    if (get().toolNames.length > 0) return; // Fetch only once
    set({ isToolNamesLoading: true, error: null });
    try {
      const names = await systemPromptApi.getSystemPromptTools();
      set({ toolNames: names, isToolNamesLoading: false });
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to fetch tool names.';
      set({ error: errorMsg, isToolNamesLoading: false });
      toast.error(errorMsg);
    }
  },

  // Tool configuration actions
  fetchAvailableTools: async () => {
    try {
      const tools = await systemPromptApi.getAllToolNames();
      set({ availableTools: tools });
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to fetch available tools.';
      set({ error: errorMsg });
      toast.error(errorMsg);
    }
  },

  setEditingPromptEnabledTools: (tools: string[] | 'all') => {
    set({ editingPromptEnabledTools: tools });
  },

  setSystemPromptEnabledTools: async (id: string, tools: string[] | 'all') => {
    try {
      const updated = await systemPromptApi.setSystemPromptEnabledTools(id, tools);
      set((state) => ({
        systemPrompts: state.systemPrompts.map((p) =>
          p.id === id ? { ...p, enabledTools: updated } : p
        ),
        editingPromptEnabledTools: updated,
      }));
      toast.success('Enabled tools updated successfully.');
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to set enabled tools.';
      set({ error: errorMsg });
      toast.error(errorMsg);
      throw err;
    }
  },

  toggleSystemPromptTool: async (id: string, toolName: string) => {
    try {
      const updated = await systemPromptApi.toggleSystemPromptTool(id, toolName);
      set((state) => ({
        systemPrompts: state.systemPrompts.map((p) =>
          p.id === id ? { ...p, enabledTools: updated } : p
        ),
        editingPromptEnabledTools: updated,
      }));
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to toggle tool.';
      set({ error: errorMsg });
      toast.error(errorMsg);
      throw err;
    }
  },
}));